import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { getStudentVerificationStatus } from '@tapp/api';
import type { GetStudentVerificationStatus200 } from '@tapp/api';
import type { AllResponseTypes, VerificationResponse } from '@sheerid/jslib';
import { SiteCountries } from '../../../../composables/useSiteCountry';
import { calculateAge } from '@tapp/layer-form/schema/dateOfBirth/dateOfBirth';

export type UseStudentVerificationProps = {
  route?: RouteLocationNormalizedGeneric;
};

export function useStudentVerification({ route = useRoute() }: UseStudentVerificationProps = {}) {
  const { isAuth, login, userinfo } = usePip();
  const siteCountry = useSiteCountry(route);

  const { studentVerification } = useRuntimeConfig().public;

  const isStudentVerificationRoute = computed(() => {
    return studentVerification.routes?.some?.((studentVerificationRoute) =>
      route.path.toLowerCase().startsWith(studentVerificationRoute.toLowerCase()),
    );
  });

  const studentVerificationProgramId = computed(() => studentVerification.programId);

  const userStudentVerification = useState<
    AllResponseTypes | VerificationResponse | GetStudentVerificationStatus200 | null
  >('userStudentVerification', () => null);

  const isInValidStudentAgeGroup = computed(() => {
    const age = calculateAge(userinfo.value?.birthdate);
    if (Number.isNaN(age)) {
      return true;
    }

    return age >= 16 && age <= 25;
  });

  const featureFlags = useFeatureFlags();

  const isStudentVerificationRequired = computed(() => {
    return Boolean(
      featureFlags.value.sheer_id_verification === true &&
        studentVerification.programId &&
        isStudentVerificationRoute.value,
    );
  });

  const userStudentVerificationStatus = computed<'verified' | 'error' | 'not_started' | 'already_subscribed'>(() => {
    if (!isStudentVerificationRequired.value) {
      return 'verified';
    }

    if (!isAuth.value || !userStudentVerification.value) {
      return 'not_started';
    }

    if ((userStudentVerification.value as GetStudentVerificationStatus200).subscription) {
      return 'already_subscribed';
    }

    switch (userStudentVerification.value.currentStep) {
      case 'success':
        return 'verified';
      case 'error':
        return 'error';
    }

    return 'not_started';
  });

  async function resolveUserStudentVerification() {
    if (!isAuth.value) {
      userStudentVerification.value = null;
      return;
    }

    try {
      const { data: response } = await getStudentVerificationStatus();

      userStudentVerification.value = response.value as GetStudentVerificationStatus200;
    } catch {
      userStudentVerification.value = null;
    }
  }

  async function verifyUserAsStudent() {
    if (!isStudentVerificationRequired.value) {
      return;
    }

    try {
      if (!isAuth.value || !userinfo.value?.sub) {
        login();
        return;
      }

      // Lazy load the SheerId library until needed
      const sheerId = await useSheerId();

      const sheerIdModal = sheerId.loadInModal(
        `https://services.sheerid.com/verify/${studentVerification.programId}/?redirectUrl=${window.location.href}&country=${siteCountry}&locale=${siteCountry === SiteCountries.BE ? 'nl-BE' : 'nl'}`,
        {
          mobileRedirect: false,
          stopPropagation: true,
        },
      );

      sheerIdModal.setViewModel({
        firstName: userinfo.value?.given_name,
        lastName: userinfo.value?.family_name,
        email: userinfo.value?.email,
        ...(userinfo.value?.birthdate && { birthDate: userinfo.value.birthdate }),
        metadata: {
          mid: useMid(route).mid.value,
          brandCode: useBrandCode().value,
          accountId: userinfo.value?.sub,
        },
      });

      sheerIdModal.setOptions({
        formFieldConfig: [
          {
            fieldId: 'birthDate',
            disabled: true,
            requireIfOptional: true,
          },
        ],
      });

      sheerIdModal.iframeInstance.on('ON_VERIFICATION_SUCCESS', (successResponse) => {
        userStudentVerification.value = successResponse;
        sheerIdModal.closeModal();
      });

      sheerIdModal.iframeInstance.on('ON_VERIFICATION_STEP_CHANGE', (response) => {
        userStudentVerification.value = response;
      });

      sheerIdModal.iframeInstance.onCleanupEvents.push(() => {
        sheerIdModal.iframeInstance.off('ON_VERIFICATION_SUCCESS');
        sheerIdModal.iframeInstance.off('ON_VERIFICATION_STEP_CHANGE');
      });
    } catch {
      userStudentVerification.value = null;
    }
  }

  return {
    studentVerificationProgramId,
    isStudentVerificationRequired,
    isStudentVerificationRoute,
    userStudentVerificationStatus,

    isInValidStudentAgeGroup,
    verifyUserAsStudent,
    resolveUserStudentVerification,
  };
}
